import KitsList from '@/components/Operational/Kits/KitsList';
import { AppPageHeader, TabContentWrapper } from '@/components/_common';
import { PageProps } from '@/types/PageProps';
import { Button, Col, Row, Statistic } from 'antd';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import {
  useDebounce,
  useDefaultFormErrorNotification,
  useReportGenerate,
} from '@/hooks';
import { KitsQueryFilters } from '@/core/domain/operational/Kits/Kits.types';
import { useHistory } from 'react-router-dom';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import useFileLoading, { FILE_FORMAT, FileType } from '@/hooks/useFileLoading';
import { ReportCallback } from '@/hooks/useReportGenerate';
import { useGenerateKitsReport } from '@/core/domain/reports/kits/Report.hooks';
import { useCountKit } from '@/core/domain/operational/Kits/Kits.hooks';

const KitsPage: React.FC<PageProps> = ({ route }) => {
  const history = useHistory();
  const intl = useIntl();
  const [queryParams, setQueryParams] = useState<KitsQueryFilters>({
    order: 'ASC',
    ...convertParams<KitsQueryFilters>({
      description: 'string',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);

  usePersistSearchParams(queryDebounce);

  const generateKitsReport = useGenerateKitsReport();
  const { openFile, downloadFile } = useReportGenerate();
  const { fileLoading, setFileLoading, setFileUnloading } = useFileLoading();
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();

  const kitCount = useCountKit();

  const handleGenerateReport = async (
    format: FileType,
    callback: ReportCallback,
  ) => {
    setFileLoading(format);
    try {
      const result = await generateKitsReport.mutateAsync(FILE_FORMAT[format]);

      if (callback) {
        callback(result);
      }
    } catch (err) {
      sendFormErrorNotification(err);
    }
    setFileUnloading(format);
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <TabContentWrapper
        title={route.name}
        permissions={route.permissions}
        extra={
          <SpaceForButton>
            <Button
              block
              type="primary"
              onClick={() =>
                handleGenerateReport('REPORT_VIEW', (result) =>
                  openFile({
                    result,
                    fileName: 'Kits.pdf',
                    fileType: 'application/pdf',
                  }),
                )
              }
              loading={fileLoading.REPORT_VIEW}
              disabled={!hasPermissions([Authorities.KITS_REGISTRATION])}
            >
              Visualizar Relatório
            </Button>

            <Button
              block
              type="primary"
              onClick={() =>
                handleGenerateReport('PDF', (result) =>
                  downloadFile({
                    result,
                    fileName: 'Kits.pdf',
                  }),
                )
              }
              loading={fileLoading.PDF}
              disabled={!hasPermissions([Authorities.KITS_REGISTRATION])}
            >
              Baixar PDF
            </Button>

            <Button
              block
              type="primary"
              onClick={() => history.push('/app/operational/kits/create')}
              disabled={!hasPermissions([Authorities.KITS_REGISTRATION])}
            >
              Cadastrar Kit
            </Button>
          </SpaceForButton>
        }
      >
        <Row gutter={[24, 24]}>
          <Col flex={1}>
            <AppPageHeader
              searchValue={queryParams?.description}
              onSearch={(value) =>
                setQueryParams((prev) => ({ ...prev, description: value }))
              }
            />
          </Col>

          <Col>
            <Statistic
              style={{ textAlign: 'right' }}
              title="Total de kits"
              value={(kitCount.data?.totalQuantity || 0).toLocaleString(
                'pt-br',
              )}
            />
          </Col>
        </Row>

        <KitsList queryParams={queryParams} />
      </TabContentWrapper>
    </>
  );
};

export default KitsPage;
